import { type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useUser, useAuth } from "@/lib/auth";
import { clsx } from "clsx";
import {
  Activity,
  Send,
  FileText,
  LayoutTemplate,
  Settings,
  CreditCard,
  Bell,
  LogOut,
} from "lucide-react";
import { SSEProvider, useSSE } from "@/hooks/use-sse";
import { EventListener } from "@/components/notifications/event-listener";
import { PeekopMark } from "@/components/brand/peekop-mark";
import { shouldEnableAppRealtime } from "@/lib/realtime-route-policy";

const NAV_ITEMS = [
  { to: "/", icon: Activity, label: "Activity", end: true },
  { to: "/send", icon: Send, label: "Send", end: false },
  { to: "/documents", icon: FileText, label: "Documents", end: false },
  { to: "/templates", icon: LayoutTemplate, label: "Templates", end: false },
  { to: "/settings", icon: Settings, label: "Settings", end: false },
  { to: "/billing", icon: CreditCard, label: "Billing", end: false },
];

function AppBrandLockup({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className="flex items-center">
      <PeekopMark
        variant="full"
        className={clsx(mobile ? "h-12" : "h-14")}
      />
    </div>
  );
}

function SidebarNav() {
  return (
    <nav className="flex flex-1 flex-col gap-1.5 px-4">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            clsx(
              "group flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-white text-slate-950 shadow-[0_10px_30px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/80"
                : "text-slate-600 hover:bg-white/80 hover:text-slate-900 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)]",
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={clsx(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-200",
                  isActive
                    ? "bg-[linear-gradient(135deg,rgba(45,212,191,0.2),rgba(255,251,235,0.95))] text-slate-950 ring-1 ring-teal-200/70"
                    : "bg-white/70 text-slate-500 ring-1 ring-slate-200/70 group-hover:bg-[linear-gradient(135deg,rgba(45,212,191,0.12),rgba(255,251,235,0.95))] group-hover:text-slate-900",
                )}
              >
                <item.icon className="h-4.5 w-4.5 shrink-0" />
              </span>
              <span className="flex-1">{item.label}</span>
              {isActive && (
                <span className="h-2 w-2 rounded-full bg-teal-400 shadow-[0_0_0_4px_rgba(204,251,241,0.9)]" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function BottomNav() {
  const location = useLocation();

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pt-1 lg:hidden">
      <div className="mx-auto flex max-w-md items-center justify-around rounded-[24px] border border-white/70 bg-white/92 px-2 py-1.5 shadow-[0_20px_50px_rgba(15,23,42,0.16)] backdrop-blur-xl">
        {NAV_ITEMS.slice(0, 5).map((item) => {
          const isActive = item.end
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={clsx(
                "flex min-w-[62px] flex-col items-center gap-1 rounded-2xl px-3 py-2 text-[11px] font-medium transition-all duration-200",
                isActive
                  ? "bg-[linear-gradient(135deg,rgba(45,212,191,0.16),rgba(255,251,235,0.98))] text-slate-950 shadow-[0_8px_24px_rgba(15,23,42,0.08)] ring-1 ring-teal-100"
                  : "text-slate-500 hover:text-slate-900",
              )}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

function ShellFrame({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { signOut } = useAuth();
  const { lastEvent, unreadCount, clearUnread, connectionState } = useSSE();

  const liveIndicator =
    connectionState === "connected"
      ? {
          label: "Live connected",
          dot: "bg-emerald-400",
          ring: "shadow-[0_0_0_4px_rgba(167,243,208,0.55)]",
          text: "text-emerald-700",
          bg: "bg-emerald-50",
        }
      : connectionState === "reconnecting"
        ? {
            label: "Reconnecting",
            dot: "bg-amber-400",
            ring: "shadow-[0_0_0_4px_rgba(253,230,138,0.55)]",
            text: "text-amber-700",
            bg: "bg-amber-50",
          }
        : {
            label: "Connecting",
            dot: "bg-slate-400",
            ring: "shadow-[0_0_0_4px_rgba(226,232,240,0.75)]",
            text: "text-slate-600",
            bg: "bg-slate-50",
          };

  return (
    <div className="flex min-h-screen">
      {/* SSE event listener for toasts */}
      <EventListener lastEvent={lastEvent} />

      {/* Sidebar - desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-white/70 bg-[linear-gradient(180deg,#f8fbff_0%,#f4f7fb_56%,#f7fbfa_100%)] shadow-[18px_0_50px_rgba(15,23,42,0.05)] lg:flex">
        {/* Logo */}
        <div className="border-b border-slate-200/70 px-6 py-5">
          <NavLink to="/" aria-label="Peekop home" className="flex flex-col gap-3">
            <AppBrandLockup />
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
              <span className="h-2 w-2 rounded-full bg-teal-400 shadow-[0_0_0_4px_rgba(204,251,241,0.85)]" />
              Live workspace
            </div>
          </NavLink>
        </div>

        {/* Navigation */}
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto py-5">
          <SidebarNav />
        </div>

        {/* User section */}
        <div className="border-t border-slate-200/70 p-4">
          <div className="rounded-[24px] border border-white/80 bg-white/88 p-3.5 shadow-[0_14px_36px_rgba(15,23,42,0.06)] backdrop-blur-sm">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Account
              </span>
              <button
                onClick={() => signOut()}
                className="rounded-xl p-2 text-slate-400 transition-all duration-200 hover:bg-slate-100 hover:text-slate-600"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt=""
                className="h-11 w-11 rounded-full object-cover ring-2 ring-white"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700 ring-2 ring-white">
                {user?.firstName?.[0] ?? "U"}
              </div>
            )}
              <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">
                {user?.fullName ?? "User"}
              </p>
              <p className="truncate text-xs text-slate-500">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          {/* Mobile logo */}
          <NavLink to="/" className="lg:hidden" aria-label="Peekop home">
            <AppBrandLockup mobile />
          </NavLink>

          {/* Spacer on desktop */}
          <div className="hidden lg:block" />

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                "hidden items-center gap-2 rounded-2xl border border-white/80 px-3 py-2 text-xs font-semibold shadow-[0_10px_25px_rgba(15,23,42,0.04)] sm:flex",
                liveIndicator.bg,
                liveIndicator.text,
              )}
            >
              <span className={clsx("h-2.5 w-2.5 rounded-full", liveIndicator.dot, liveIndicator.ring)} />
              {liveIndicator.label}
            </div>

            <button
              onClick={clearUnread}
              className="relative rounded-2xl border border-white/80 bg-white/90 p-2.5 text-slate-400 shadow-[0_10px_25px_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:text-slate-600"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {/* Mobile user avatar */}
            <div className="lg:hidden">
              {user?.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
                  {user?.firstName?.[0] ?? "U"}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="app-bg flex-1 px-4 py-6 sm:px-6 lg:px-8 pb-20 lg:pb-6">
          {children}
        </main>
      </div>

      {/* Bottom nav - mobile */}
      <BottomNav />
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const realtimeEnabled = shouldEnableAppRealtime(location.pathname);

  return (
    <SSEProvider enabled={realtimeEnabled}>
      <ShellFrame>{children}</ShellFrame>
    </SSEProvider>
  );
}
